import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../hooks/useApi';
import { Card, Spinner, Button } from '../components/ui';
import type { DashboardStats } from '@ns-b2b/shared';

export function Dashboard() {
    const { user } = useAuth();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        setIsLoading(true);
        const { data } = await api<DashboardStats>('/api/statistics');
        if (data) setStats(data);
        setIsLoading(false);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center">
                    <Spinner size="lg" />
                    <p className="text-neutral-500 mt-4">กำลังโหลดข้อมูล...</p>
                </div>
            </div>
        );
    }

    const teamColors: Record<string, string> = {
        A: 'from-orange-500 to-red-500',
        B: 'from-blue-500 to-indigo-500',
        C: 'from-green-500 to-emerald-500',
        D: 'from-purple-500 to-pink-500',
    };

    const teamBgs: Record<string, string> = {
        A: 'bg-orange-500',
        B: 'bg-blue-500',
        C: 'bg-green-500',
        D: 'bg-purple-500',
    };

    const totalTeams = Object.keys(stats?.teamStats || {}).length;
    const avgPerTeam = totalTeams > 0 ? Math.round((stats?.totalVisits || 0) / totalTeams) : 0;

    // Line Chart Logic
    const trendData = stats?.monthlyTrend || [];
    const maxCount = Math.max(...trendData.map(d => Number(d.count)), 5); // Minimum max of 5 usually looks better

    // Calculate coordinates (0-100 scale)
    const points = trendData.map((d, i) => {
        const x = (i / (trendData.length - 1)) * 100;
        // Leave 20% margin at top, 10% at bottom for visual breathing room
        const y = 90 - (Number(d.count) / maxCount) * 80;
        return { x, y, value: d.count, label: d.month };
    });

    // Generate Smooth Path using Catmull-Rom spline logic converted to Cubic Bezier
    const getSmoothPath = (pts: typeof points, closeArea = false) => {
        if (pts.length < 2) return '';

        const line = (pointA: typeof points[0], pointB: typeof points[0]) => {
            const lengthX = pointB.x - pointA.x;
            const lengthY = pointB.y - pointA.y;
            return {
                length: Math.sqrt(Math.pow(lengthX, 2) + Math.pow(lengthY, 2)),
                angle: Math.atan2(lengthY, lengthX)
            };
        };

        const controlPoint = (current: typeof points[0], previous: typeof points[0], next: typeof points[0], reverse?: boolean) => {
            const p = previous || current;
            const n = next || current;
            const o = line(p, n);
            const angle = o.angle + (reverse ? Math.PI : 0);
            const length = o.length * 0.15; // Smoothing factor (0.15 is pleasant)
            const x = current.x + Math.cos(angle) * length;
            const y = current.y + Math.sin(angle) * length;
            return { x, y };
        };

        const bezierCommand = (point: typeof points[0], i: number, a: typeof points) => {
            const cps = controlPoint(a[i - 1], a[i - 2], point);
            const cpe = controlPoint(point, a[i - 1], a[i + 1], true);
            return `C ${cps.x.toFixed(2)},${cps.y.toFixed(2)} ${cpe.x.toFixed(2)},${cpe.y.toFixed(2)} ${point.x.toFixed(2)},${point.y.toFixed(2)}`;
        };

        const d = pts.reduce((acc, point, i, a) =>
            i === 0 ? `M ${point.x},${point.y}` : `${acc} ${bezierCommand(point, i, a)}`
            , '');

        if (closeArea) {
            return `${d} L 100,100 L 0,100 Z`;
        }
        return d;
    };

    const pathD = getSmoothPath(points);
    const areaD = getSmoothPath(points, true);

    return (
        <div className="space-y-8">
            {/* Hero Section */}
            <div className="relative overflow-hidden bg-gradient-to-r from-primary to-primary-dark rounded-3xl p-8 text-white">
                <div className="relative z-10">
                    <p className="text-white/80 mb-2">สวัสดี 👋</p>
                    <h1 className="text-3xl font-bold mb-2">{user?.name}</h1>
                    <p className="text-white/70">ยินดีต้อนรับสู่ระบบ NS Small B2B</p>
                </div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 right-20 w-32 h-32 bg-white/10 rounded-full translate-y-1/2" />
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard title="หน้างานทั้งหมด" value={stats?.totalVisits || 0} icon="📍" gradient="from-orange-500 to-amber-500" />
                <StatCard title="เดือนนี้" value={stats?.thisMonthVisits || 0} icon="📅" gradient="from-blue-500 to-cyan-500" />
                <StatCard title="จำนวนทีม" value={totalTeams} icon="👥" gradient="from-green-500 to-emerald-500" />
                <StatCard title="เฉลี่ย/ทีม" value={avgPerTeam} icon="📊" gradient="from-pink-500 to-rose-500" />
            </div>

            {/* Team Performance & Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card padding="lg">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-bold text-neutral-900">ผลงานรายทีม</h2>
                        <Button onClick={loadStats} variant="ghost" size="sm">🔄</Button>
                    </div>
                    <div className="space-y-4">
                        {Object.entries(stats?.teamStats || {}).map(([team, count]) => {
                            const total = stats?.totalVisits || 1;
                            const percentage = Math.round((Number(count) / total) * 100);
                            const color = teamColors[team] || 'from-gray-500 to-gray-600';
                            const bgColor = teamBgs[team] || 'bg-gray-500';
                            return (
                                <div key={team} className="group">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-xl ${bgColor} flex items-center justify-center text-white font-bold shadow-lg`}>
                                                {team}
                                            </div>
                                            <div>
                                                <p className="font-semibold text-neutral-800">ทีม {team}</p>
                                                <p className="text-sm text-neutral-500">{count} หน้างาน</p>
                                            </div>
                                        </div>
                                        <span className="text-2xl font-bold text-neutral-900">{percentage}%</span>
                                    </div>
                                    <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                                        <div className={`h-full bg-gradient-to-r ${color} rounded-full transition-all duration-1000 ease-out`} style={{ width: `${percentage}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card>
                <Card padding="lg">
                    <h2 className="text-lg font-bold text-neutral-900 mb-6">ดำเนินการด่วน</h2>
                    <div className="grid grid-cols-2 gap-4">
                        <QuickAction icon="📝" label="เพิ่มหน้างาน" description="บันทึกข้อมูลใหม่" onClick={() => window.location.href = '?tab=visit-form'} />
                        <QuickAction icon="📋" label="ดูรายการ" description="รายการทั้งหมด" onClick={() => window.location.href = '?tab=visits-list'} />
                        <QuickAction icon="🗺️" label="แผนที่" description="ดูตำแหน่ง" onClick={() => window.location.href = '?tab=map'} />
                        <QuickAction icon="🔁" label="ติดตาม" description="ติดตามลูกค้า" onClick={() => window.location.href = '?tab=followup'} />
                    </div>
                </Card>
            </div>

            {/* Monthly Trend Chart */}
            <Card padding="lg">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-lg font-bold text-neutral-900">แนวโน้มรายเดือน</h2>
                        <p className="text-sm text-neutral-500">จำนวนหน้างานย้อนหลัง</p>
                    </div>
                </div>

                {trendData.length > 0 ? (
                    <div className="relative h-72 pt-4 pb-8 select-none">
                        {/* SVG Chart Background */}
                        <div className="absolute inset-0 top-0 bottom-8 left-4 right-4">
                            <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100" preserveAspectRatio="none">
                                {/* Defs */}
                                <defs>
                                    <linearGradient id="areaGradient" x1="0" x2="0" y1="0" y2="1">
                                        <stop offset="0%" stopColor="#F97316" stopOpacity="0.2" />
                                        <stop offset="90%" stopColor="#F97316" stopOpacity="0" />
                                    </linearGradient>
                                    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                                        <feDropShadow dx="0" dy="4" stdDeviation="3" floodColor="#F97316" floodOpacity="0.3" />
                                    </filter>
                                </defs>

                                {/* Grid Lines (Dashed) */}
                                {[0, 25, 50, 75, 100].map(p => (
                                    <line key={p} x1="0" y1={p} x2="100" y2={p} stroke="#E5E5E5" strokeWidth="0.5" strokeDasharray="2,2" vectorEffect="non-scaling-stroke" />
                                ))}

                                {/* Area Fill */}
                                <path d={areaD} fill="url(#areaGradient)" className="transition-all duration-500" />

                                {/* Line Path (with shadow) */}
                                <path
                                    d={pathD}
                                    fill="none"
                                    stroke="#F97316"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    filter="url(#shadow)"
                                    vectorEffect="non-scaling-stroke"
                                    className="transition-all duration-500"
                                />
                            </svg>
                        </div>

                        {/* Interactive Points (HTML Layer to prevent distortion) */}
                        <div className="absolute inset-0 top-0 bottom-8 left-4 right-4 pointer-events-none">
                            {points.map((p, i) => (
                                <div
                                    key={i}
                                    className="absolute -translate-x-1/2 -translate-y-1/2 group pointer-events-auto"
                                    style={{ left: `${p.x}%`, top: `${p.y}%` }}
                                >
                                    {/* Outer glow ring */}
                                    <div className="w-8 h-8 rounded-full bg-primary/10 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity" />

                                    {/* The Dot */}
                                    <div className="w-3.5 h-3.5 bg-white border-[3px] border-primary rounded-full shadow-sm hover:scale-125 transition-transform cursor-pointer relative z-10" />

                                    {/* Tooltip */}
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0 z-20">
                                        <div className="bg-neutral-900 text-white text-xs px-3 py-1.5 rounded-lg shadow-xl whitespace-nowrap font-medium flex flex-col items-center">
                                            <span className="text-lg font-bold">{p.value}</span>
                                            <span className="text-white/70 text-[10px] uppercase tracking-wider">หน้างาน</span>
                                        </div>
                                        {/* Little triangular arrow */}
                                        <div className="w-2 h-2 bg-neutral-900 rotate-45 absolute -bottom-1 left-1/2 -translate-x-1/2" />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* X-Axis Labels */}
                        <div className="absolute bottom-0 left-4 right-4 flex justify-between items-end h-8 text-neutral-400 text-xs font-medium">
                            {points.map((p, i) => (
                                <div key={i} className="flex-1 text-center truncate px-1">
                                    {p.label}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-64 text-neutral-400 bg-neutral-50/50 rounded-2xl border-2 border-dashed border-neutral-100">
                        <div className="text-center">
                            <span className="text-4xl mb-2 block opacity-50">📊</span>
                            <p>ยังไม่มีข้อมูลเพียงพอสำหรับการวิเคราะห์</p>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}

function StatCard({ title, value, icon, gradient }: { title: string; value: number | string; icon: string; gradient: string }) {
    return (
        <div className={`relative overflow-hidden bg-gradient-to-br ${gradient} rounded-2xl p-5 text-white shadow-lg`}>
            <div className="relative z-10">
                <p className="text-white/80 text-sm font-medium opacity-90">{title}</p>
                <p className="text-3xl font-bold mt-2 tracking-tight">{value}</p>
            </div>
            <div className="absolute right-3 top-3 text-4xl opacity-20 mix-blend-overlay">{icon}</div>
            <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-white/10 rounded-full blur-2xl" />
        </div>
    );
}

function QuickAction({ icon, label, description, onClick }: { icon: string; label: string; description: string; onClick: () => void }) {
    return (
        <button onClick={onClick} className="flex flex-col items-center p-5 bg-white border border-neutral-100 hover:border-primary/20 hover:bg-primary/5 rounded-2xl transition-all duration-300 group shadow-sm hover:shadow-md">
            <span className="text-3xl mb-3 group-hover:scale-110 transition-transform duration-300 filter grayscale group-hover:grayscale-0">{icon}</span>
            <p className="font-semibold text-neutral-800 group-hover:text-primary transition-colors">{label}</p>
            <p className="text-xs text-neutral-500 mt-1">{description}</p>
        </button>
    );
}
